import type {
  BlockingMode,
  Category,
  ColorScheme,
  ConsentKitConfig,
  Layout,
  LocaleCode,
  Position,
  Region,
  ThemeName,
  UserConfig,
} from "../types";
import { DEFAULT_CONFIG } from "./defaults";

/**
 * The single seam where configuration enters the library. Merge order, last wins:
 *   DEFAULT_CONFIG  <-  window.consentKitConfig  <-  data-* attributes on the script tag
 *
 * A hosted dashboard would slot in here (read `data-site-id`, fetch JSON, merge in the
 * same position as `window.consentKitConfig`) without any other file needing to change.
 */
export function resolveConfig(script: HTMLScriptElement | null, global?: UserConfig): ConsentKitConfig {
  const merged = mergeConfig(DEFAULT_CONFIG, global ?? {});
  return mergeConfig(merged, script ? readDataAttributes(script) : {});
}

function mergeConfig(base: ConsentKitConfig, patch: UserConfig): ConsentKitConfig {
  return {
    ...base,
    ...stripUndefined(patch as Partial<ConsentKitConfig>),
    categories: { ...base.categories, ...stripUndefined(patch.categories ?? {}) },
    theme: {
      ...base.theme,
      ...stripUndefined(patch.theme ?? {}),
      tokens: { ...base.theme.tokens, ...(patch.theme?.tokens ?? {}) },
      darkTokens: { ...base.theme.darkTokens, ...(patch.theme?.darkTokens ?? {}) },
    },
    controller: { ...base.controller, ...stripUndefined(patch.controller ?? {}) },
    texts: patch.texts ?? base.texts,
  };
}

function stripUndefined<T extends object>(value: T): Partial<T> {
  const out: Partial<T> = {};
  for (const key of Object.keys(value) as (keyof T)[]) {
    if (value[key] !== undefined) out[key] = value[key];
  }
  return out;
}

const BLOCKING_MODES: BlockingMode[] = ["manual", "known-vendors", "strict", "all-third-party"];
const LAYOUTS: Layout[] = ["box", "bar", "center"];
const POSITIONS: Position[] = ["bottom-left", "bottom-right", "bottom-center", "top-center"];
const THEMES: ThemeName[] = ["porcelain", "midnight", "mono", "sky"];
const SCHEMES: ColorScheme[] = ["light", "dark", "auto"];
const LOCALES: LocaleCode[] = ["de", "en"];
const CATEGORIES: Category[] = ["functional", "analytics", "marketing"];
const REGIONS: Region[] = ["eu", "us", "auto"];

/**
 * `data-*` attributes cover the common cases so a site can be configured without any
 * inline JavaScript — which matters on CMS platforms where you only get a snippet box.
 */
function readDataAttributes(script: HTMLScriptElement): UserConfig {
  const data = script.dataset;
  const config: UserConfig = {};

  const vendors = splitList(data["vendors"]);
  if (vendors.length > 0) config.vendors = vendors;

  const allowlist = splitList(data["allowlist"]);
  if (allowlist.length > 0) config.allowlist = allowlist;

  const blocking = pick(data["blocking"], BLOCKING_MODES);
  if (blocking) config.blocking = blocking;

  const region = pick(data["region"], REGIONS);
  if (region) config.region = region;

  const optOut = splitList(data["optOut"]).filter((c): c is Exclude<Category, "necessary"> =>
    (CATEGORIES as string[]).includes(c),
  );
  if (optOut.length > 0) config.optOutCategories = optOut;

  const honorGpc = parseBool(data["honorGpc"]);
  if (honorGpc !== undefined) config.honorGpc = honorGpc;

  const layout = pick(data["layout"], LAYOUTS);
  if (layout) config.layout = layout;

  const position = pick(data["position"], POSITIONS);
  if (position) config.position = position;

  const enabled = splitList(data["categories"]).filter((c): c is Exclude<Category, "necessary"> =>
    (CATEGORIES as string[]).includes(c),
  );
  if (enabled.length > 0) config.categories = { enabled };

  const theme: UserConfig["theme"] = {};
  const preset = pick(data["theme"], THEMES);
  if (preset) theme.preset = preset;
  const scheme = pick(data["scheme"], SCHEMES);
  if (scheme) theme.scheme = scheme;
  if (data["accent"]) theme.accent = data["accent"];
  const tokens = parseTokens(data["tokens"]);
  if (tokens) theme.tokens = tokens;
  if (data["radius"]) theme.tokens = { ...theme.tokens, radius: data["radius"] };
  if (Object.keys(theme).length > 0) config.theme = theme;

  const lang = data["lang"];
  if (lang === "auto") config.locale = "auto";
  else {
    const locale = pick(lang, LOCALES);
    if (locale) config.locale = locale;
  }

  const controller: UserConfig["controller"] = {};
  if (data["controller"]) controller.name = data["controller"];
  if (data["privacyUrl"]) controller.privacyUrl = data["privacyUrl"];
  if (data["imprintUrl"]) controller.imprintUrl = data["imprintUrl"];
  if (Object.keys(controller).length > 0) config.controller = controller;

  if (data["configVersion"]) config.configVersion = data["configVersion"];
  if (data["cookieName"]) config.cookieName = data["cookieName"];
  if (data["cookieDomain"]) config.cookieDomain = data["cookieDomain"];
  if (data["recordEndpoint"]) config.recordEndpoint = data["recordEndpoint"];

  const lifetime = Number(data["lifetimeDays"]);
  if (Number.isFinite(lifetime) && lifetime > 0) config.consentLifetimeDays = lifetime;

  const compact = parseBool(data["compact"]);
  if (compact !== undefined) config.compact = compact;
  const revisit = parseBool(data["revisit"]);
  if (revisit !== undefined) config.revisitButton = revisit;
  const alwaysShow = parseBool(data["alwaysShowBanner"]);
  if (alwaysShow !== undefined) config.alwaysShowBanner = alwaysShow;
  const reload = parseBool(data["reloadOnRevoke"]);
  if (reload !== undefined) config.reloadOnRevoke = reload;
  const shadow = parseBool(data["shadowDom"]);
  if (shadow !== undefined) config.shadowDom = shadow;
  const debug = parseBool(data["debug"]);
  if (debug !== undefined) config.debug = debug;

  return config;
}

function splitList(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

function pick<T extends string>(value: string | undefined, allowed: T[]): T | undefined {
  return value !== undefined && (allowed as string[]).includes(value) ? (value as T) : undefined;
}

function parseBool(value: string | undefined): boolean | undefined {
  if (value === undefined || value === "") return undefined;
  return value !== "false" && value !== "0" && value !== "off";
}

/** `data-tokens='{"accent":"#f00"}'` — advanced styling without an inline script block. */
function parseTokens(value: string | undefined): Record<string, string> | undefined {
  if (!value) return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== "object") return undefined;
    const out: Record<string, string> = {};
    for (const [key, raw] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof raw === "string") out[key] = raw;
    }
    return out;
  } catch {
    return undefined;
  }
}
